import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
} from '@angular/core';
import { IonButton } from '@ionic/angular/standalone';
import { twMerge } from '@luggagehero/shared/util';
import { SvgIcons } from '@ngneat/svg-icon';

import { SharedUiSpinnerComponent } from '../spinner/shared-ui-spinner.component';
import { SharedUiSvgIconComponent } from '../svg-icon/shared-ui-svg-icon.component';

export type SharedUiButtonSize = 'small' | 'medium' | 'large';
export type SharedUiButtonStyle =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'link'
  | 'neutral';
export type SharedUiButtonIcon = 'forward' | 'back' | 'add' | 'help' | 'custom';
export type SharedUiButtonVariant = 'filled' | 'ghost' | 'outline';

const SizeMap: Record<SharedUiButtonSize, string> = {
  small: 'tw-px-3 tw-py-2 tw-text-xs',
  medium: 'tw-px-4 tw-py-2 tw-text-base',
  large: 'tw-px-8 tw-py-6 tw-text-lg',
};

// 1) Style defines the color via text-* (currentColor source)
const StyleBase: Record<SharedUiButtonStyle, string> = {
  default: 'tw-text-neutral-950',
  primary: 'tw-text-white tw-bg-primary',
  success: 'tw-text-success',
  warning: 'tw-text-orange',
  danger: 'tw-text-danger',
  info: 'tw-text-neutral-950',
  link: 'tw-text-primary',
  neutral: 'tw-text-neutral-950',
};

// 2) Variant consumes currentColor
const VariantBase: Record<SharedUiButtonVariant, string> = {
  ghost: 'tw-bg-transparent tw-text-current hover:tw-bg-gray-100 tw-border-none',
  outline: 'tw-border tw-border-solid tw-border-current tw-bg-transparent hover:tw-bg-gray-50 tw-text-current',
  filled: 'tw-bg-current tw-text-white',
};

// 3) Per-combination overrides (add/replace specifics)
const ComboOverrides: Partial<Record<`${SharedUiButtonStyle}:${SharedUiButtonVariant}`, string>> = {
  // Neutral (exact ask)
  'neutral:filled': 'tw-text-white tw-bg-neutral-950 hover:tw-bg-neutral-800 disabled:tw-bg-neutral-300',
  'neutral:ghost': 'tw-text-neutral-950',
  'neutral:outline': 'tw-border-neutral-950 tw-text-neutral-950',

  // Warning uses orange tokens explicitly
  'warning:filled': 'tw-text-white tw-bg-orange hover:tw-bg-orange-dark disabled:tw-bg-orange-light',
  'warning:outline': 'tw-border-orange tw-text-orange',

  // Info looks like a light button
  'info:filled':
    'tw-bg-white tw-text-neutral-950 tw-border tw-border-solid tw-border-neutral-300 hover:tw-bg-neutral-100',
  'info:outline': 'tw-border-neutral-300',

  // Default should be light gray filled, dark text
  'default:filled': 'tw-bg-gray-200 tw-text-neutral-950',
  'default:outline': 'tw-border-neutral-300 tw-text-neutral-950',
  'default:ghost': 'tw-text-neutral-950',

  // Common outlines for color styles
  'primary:outline': 'tw-border-primary tw-text-primary',
  'success:outline': 'tw-border-success tw-text-success',
  'danger:outline': 'tw-border-danger tw-text-danger',

  // Link should never look boxed
  'link:filled': 'tw-bg-transparent tw-text-primary',
  'link:outline': 'tw-border-none tw-text-primary tw-bg-transparent',
  'link:ghost': 'tw-text-primary tw-border-none',

  'primary:filled': 'tw-bg-primary hover:tw-bg-primary-dark disabled:tw-bg-primary-light',
};

@Component({
  selector: 'lh-shared-ui-button',
  imports: [CommonModule, IonButton, SharedUiSpinnerComponent, SharedUiSvgIconComponent],
  templateUrl: './shared-ui-button.component.html',
  styleUrl: './shared-ui-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw-block ' },
})
export class SharedUiButtonComponent {
  @Input() public type: HTMLButtonElement['type'] = 'button';
  @Input() public shadow = true;

  @Input()
  @HostBinding('attr.block')
  public block = false;

  @Input() public round = false;
  @Input() public rounded = false;
  @Input() public selected = false;
  /**
   * Exposes the button as a toggle (`aria-pressed`) — for a button that is one of a set of choices,
   * so a screen reader says which is chosen. `null` (default) leaves an ordinary button unannounced.
   */
  @Input() public ariaPressed: boolean | null = null;
  @Input() public disabled = false;
  @Input() public nativeLookAndFeel = false;
  @Input() public size: SharedUiButtonSize = 'medium';
  @Input() public style: SharedUiButtonStyle = 'primary';
  @Input() public icon: SharedUiButtonIcon | null = null;
  @Input() public svgIconName?: SvgIcons;
  @Input() public variant: SharedUiButtonVariant = 'filled';

  @Input()
  @HostBinding('attr.tailwind')
  public tailwind = false;

  @Input() public textAlign: 'left' | 'center' | 'right' = 'center';

  @Output() public readonly press = new EventEmitter<Event>();

  iconSize = computed(() => {
    switch (this.size) {
      case 'small':
        return 'sm';
      case 'medium':
        return 'lg';
      case 'large':
        return 'xl';
      default:
        return 'md';
    }
  });

  private _isLoading = false;
  private readonly cd = inject(ChangeDetectorRef);

  public get isLoading(): boolean {
    return this._isLoading;
  }
  @Input() public set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  public get nativeButtonColor(): string {
    switch (this.style) {
      case 'primary':
        return 'primary';
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      case 'info':
        return 'white';
      case 'link':
        return 'primary';
      case 'default':
      default:
        return 'light';
    }
  }

  public get nativeButtonFill(): 'clear' | 'outline' | 'solid' {
    switch (this.style) {
      case 'info':
        return 'outline';
      default:
        return 'solid';
    }
  }

  public get nativeButtonSize(): 'small' | 'default' | 'large' {
    switch (this.size) {
      case 'small':
        return 'small';
      case 'large':
        return 'large';
      case 'medium':
      default:
        return 'default';
    }
  }

  public onClick(event: Event): void {
    if (this.disabled || this.isLoading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.press.emit(event);
  }

  public get tailwindClasses(): string {
    const base = [
      'tw-rounded-lg',
      'tw-font-medium tw-inline-flex tw-justify-center tw-items-center tw-gap-2',
      'tw-transition-colors tw-transition-all tw-duration-200',
      'tw-text-nowrap tw-w-fit',
      SizeMap[this.size],
    ];

    const out: string[] = [...base];

    if (this.block) out.push('tw-w-full');

    // Style provides text color; variant consumes via currentColor
    out.push(StyleBase[this.style]);
    if (this.variant) {
      out.push(VariantBase[this.variant]);
      const combo = `${this.style}:${this.variant}` as const;
      if (ComboOverrides[combo]) out.push(ComboOverrides[combo]);
    }

    if (this.disabled) {
      out.push('tw-cursor-not-allowed');
    }
    return twMerge(out.join(' '));
  }
}
